import React from 'react';
import PropTypes from 'prop-types';
import {
  Button, Modal, InputGroup, FormControl,
} from 'react-bootstrap';
import { v4 as uuid, } from 'uuid';
import Confirm from '../confirm/Confirm';
import FieldSet from '../fieldset/FieldSet';
import Alert from '../alert/Alert';
import AccountLinkFactory from '../../service/AccountLinkFactory';
import StringUtils from '../../modules/StringUtils';

class AccountLinkForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      mode: AccountLinkForm.UNLINK,
      linkOpen: false,
    };
  }

  static LINK = 'LINK';

  static UNLINK = 'UNLINK';

  handleShow() {
    this.setState({
      show: true,
    });
  }

  handleClose() {
    this.setState({
      show: false,
      mode: AccountLinkForm.UNLINK,
    });
  }

  toggleMode(mode) {
    this.setState({
      mode: mode === AccountLinkForm.LINK ? AccountLinkForm.UNLINK : AccountLinkForm.LINK,
      status: null,
    });
  }

  buildStatus(message, options = {}) {
    const {
      alertVariant = 'secondary',
      header = '',
      buttonVariant = 'outline-secondary',
      onDismiss = () => this.dismissStatus(),
      buttonText = 'Okay',
    } = options;
    return (
      <Alert
        body={message}
        alertVariant={alertVariant}
        buttonVariant={buttonVariant}
        header={header}
        onDismiss={onDismiss}
        buttonText={buttonText}
      />
    );
  }

  buildSuccessStatus(message) {
    return this.buildStatus(message, {
      buttonVariant: 'outline-success',
      alertVariant: 'success',
      header: 'Success',
    });
  }

  buildFailureStatus(message) {
    return this.buildStatus(message, {
      buttonVariant: 'outline-danger',
      alertVariant: 'danger',
      header: 'Failure',
    });
  }

  formatCustomerIds(customerIds) {
    const { serviceOfferingId, id, } = this.props;
    const { unlinkOpen = {}, isLoading = false, } = this.state;
    if (!customerIds) {
      return null;
    }

    const handleUnLinkOpen = (customerId) => {
      this.setState({
        unlinkOpen: {
          open: true,
          id: customerId,
        },
      });
    };

    const handleUnLinkClose = () => {
      this.setState({
        unlinkOpen: {
          open: false,
          id: null,
        },
      });
    };

    const getConfirmationPrompt = (customerId) => {
      const isSelected = unlinkOpen.id === customerId;
      if (!isSelected) {
        return null;
      }
      return (
        <Confirm
          isLoading={isLoading}
          title="Unlink Customer Confirmation"
          content="Are you sure you want to unlink this customer?"
          open={unlinkOpen.open}
          onClose={handleUnLinkClose}
          onYes={() => this.unlinkCustomer({ customerId, serviceOfferingId, id, })}
          onNo={handleUnLinkClose}
        />
      );
    };

    return (
      <FieldSet title="Customer IDs">
        {customerIds.map(customerId => (
          <InputGroup className="mt-2" key={uuid()}>
            <FormControl value={customerId} readOnly />
            <Button variant="danger" onClick={() => handleUnLinkOpen(customerId)}>
              Unlink
            </Button>
            {getConfirmationPrompt(customerId)}
          </InputGroup>
        ))}
      </FieldSet>
    );
  }

  dismissStatus() {
    this.setState({
      status: null,
    });
  }

  formatLinkInput() {
    const { serviceOfferingId, id, } = this.props;
    const { linkOpen, isLoading, invalidInputMessage, } = this.state;

    const onChange = (event) => {
      let { value, } = event.currentTarget;
      if (value && value.length > 0) {
        value = value.trim();
        if (!StringUtils.isValidUUID(value)) {
          this.setState({
            invalidInputMessage: 'Input is not valid.',
          });
        } else {
          this.setState({
            invalidInputMessage: '',
          });
        }
      }
      this.setState({
        linkingId: value,
      });
    };

    const handlerLinkOpen = () => {
      this.setState({
        linkOpen: true,
      });
    };
    const handlerLinkClose = () => {
      this.setState({
        linkOpen: false,
      });
    };

    const onLinkCustomer = () => {
      const { linkingId, } = this.state;
      this.linkCustomer({ customerId: linkingId, serviceOfferingId, id, });
    };

    return (
      <div>
        <InputGroup className="mt-2">
          <FormControl placeholder="Enter a customer Id to link" onChange={onChange} />
          <Button
            variant="success"
            disabled={invalidInputMessage && invalidInputMessage.length > 0}
            onClick={handlerLinkOpen}
          >
            Link
          </Button>
          <Confirm
            isLoading={isLoading}
            title="Link Customer Confirmation"
            content="Are you sure you want to link this customer?"
            open={linkOpen}
            onClose={handlerLinkClose}
            onYes={onLinkCustomer}
            onNo={handlerLinkClose}
          />
        </InputGroup>
        <span className="small text-danger">{invalidInputMessage}</span>
      </div>
    );
  }

  showBody(mode) {
    const { customerIds, } = this.props;
    if (mode === AccountLinkForm.LINK) {
      return this.formatLinkInput();
    }
    return this.formatCustomerIds(customerIds);
  }

  async linkCustomer({ customerId, id, serviceOfferingId, }) {
    this.setState({
      isLoading: true,
      status: null,
    });
    const linker = await AccountLinkFactory.getLinker({ serviceOfferingId, });
    try {
      await linker
        .link({ serviceOfferingId, accountId: id, customerId, });
      this.setState({
        status: this.buildSuccessStatus(`Customer Id ${customerId} has been linked!`),
      });
    } catch (e) {
      console.error(`Unable to link customer Id ${customerId}`, e);
      this.setState({
        status: this.buildFailureStatus(`Unable to link customer Id ${customerId}`),
      });
    }
    this.setState({
      isLoading: false,
      linkOpen: false,
    });
  }

  async unlinkCustomer({ customerId, serviceOfferingId, id, }) {
    const linker = await AccountLinkFactory.getUnlinker({ serviceOfferingId, });
    this.setState({
      isLoading: true,
      status: null,
    });
    try {
      await linker.unlink({ serviceOfferingId, accountId: id, customerId, });
      this.setState({
        status: this.buildSuccessStatus(`Customer Id ${customerId} has been unlinked!`),
      });
    } catch (error) {
      console.error('Unable to unlink customer Id', error);
      this.setState({
        status: this.buildFailureStatus(`Unable to unlink customer Id ${customerId}`),
      });
    }
    this.setState({
      isLoading: false,
      unlinkOpen: {},
    });
  }

  render() {
    const { title, modalSize, } = this.props;
    const { show = false, mode, status, } = this.state;
    return (
      <div>
        <Button variant="primary" onClick={() => this.handleShow()}>
          {title}
        </Button>
        <Modal show={show} onHide={() => this.handleClose()} size={modalSize}>
          <Modal.Header closeButton>
            <Modal.Title>{title}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <button
              className="btn btn-outline-primary"
              type="button"
              onClick={() => this.toggleMode(mode)}
            >
              {`toggle/${mode !== AccountLinkForm.LINK ? 'link' : 'unlink'}`}
            </button>
            {this.showBody(mode)}
            {status}
          </Modal.Body>
        </Modal>
      </div>
    );
  }
}

AccountLinkForm.defaultProps = {
  title: 'Link/Unlink',
  modalSize: 'xl',
};

AccountLinkForm.propTypes = {
  title: PropTypes.string,
  serviceOfferingId: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  customerIds: PropTypes.array.isRequired,
  modalSize: PropTypes.string,
};

export default AccountLinkForm;
